import React, { FC, ReactNode, useRef } from 'react';
import { Box, type SxProps, type Theme, useTheme } from '@mui/material';
import { mergeSx } from 'merge-sx';

import useClientSize from '@/hooks/useClientSize';
import { navbar, NAVBAR_FULL_WIDTH_CSS_VAR, transitions } from '@/theme/constants';

interface StickyHeadProps {
  children: ReactNode;
  sx?: SxProps<Theme>;
}

const StickyHead: FC<StickyHeadProps> = ({ children, sx }) => {
  const theme = useTheme();
  const { spacing } = theme;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { width: clientWidth } = useClientSize();

  return (
    <Box
      ref={containerRef}
      mr={-5}
      ml={-5}
      pr={5}
      pl={5}
      sx={mergeSx<Theme>(
        {
          position: 'sticky',
          top: containerRef.current?.parentElement?.offsetTop,
          zIndex: 1,
          bgcolor: 'grey.50',
          willChange: 'transform',
          transition: [transitions.height_0_3, transitions.width, transitions.inset].join(', '),
          insetInlineStart: `var(${NAVBAR_FULL_WIDTH_CSS_VAR})`,
          width: `calc(${clientWidth}px - var(${NAVBAR_FULL_WIDTH_CSS_VAR}))`,
          [`.Navbar-closed &`]: {
            insetInlineStart: `${navbar.collapsedWidth}px`,
            width: `${clientWidth - navbar.collapsedWidth}px`
          },
          '&::after': {
            content: '""',
            width: '100%',
            position: 'absolute',
            bottom: 0,
            left: 0,
            mb: spacing(-2),
            height: spacing(2),
            bgcolor: 'grey.50'
          }
        },
        sx
      )}
    >
      {children}
    </Box>
  );
};

export default StickyHead;
